import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef, useState } from 'react';
import { useForm, UseFormReturn, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import actions from '../../../../modules/auth';
import { getThrottlingRetryAfterDate } from '../../../../modules/auth/selectors';
import { useOryLoginApi } from '../../../../ory/hooks/oryApi';
import { IUseOryResponseParser, useOryResponseParser } from '../../../../ory/hooks/oryResponseParser';
import { useOryUserLogin } from '../../../../ory/hooks/oryUserLogin/useOryUserLogin';
import { useLoginSsoOrganizations } from '../../hook/useLoginSsoOrganizations';
import { samlLogin } from './loginWithSAML';
import { defaultValues, getLoginFormValidationSchema, ILoginFormData, ISSOLoginFormData } from './validationScheme';

export interface IUseLoginForm {
  retryAfterDate: Date | undefined;
  handleFormSubmit: (values: ILoginFormData | ISSOLoginFormData) => Promise<void>;
  isSSOLogin: boolean;
  isOryApiFetching: boolean;
  formData: UseFormReturn<ILoginFormData | ISSOLoginFormData, any, ILoginFormData | ISSOLoginFormData>;
  getFormAlertMessage: IUseOryResponseParser['getFormAlertMessage'];
}

export function useLoginForm({ onOrySecondFactor }: { onOrySecondFactor: () => void }): IUseLoginForm {
  const dispatch = useDispatch();
  const isFirstRender = useRef(true);
  const retryAfterDate = useSelector(getThrottlingRetryAfterDate);
  const { formatMessage } = useIntl();
  const [isSSOLogin, setIsSSOLogin] = useState(false);
  const formData = useForm<ILoginFormData | ISSOLoginFormData, any, ILoginFormData | ISSOLoginFormData>({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: yupResolver(getLoginFormValidationSchema({ formatMessage, isPasswordMandatory: !isSSOLogin })),
  });
  const email = useWatch({ control: formData.control, name: 'email' });
  const { ssoOrganizationId } = useLoginSsoOrganizations({
    email,
    setIsSSOLogin,
  });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    formData.trigger();
  }, [ssoOrganizationId]);

  const { getFormAlertMessage, isOryApiFetching, loginOryUser } = useOryUserLogin();
  const { isOryApiFetching: isOrySAMLApiFetching, loginToOryWithSAML } = useOryLoginApi();
  const { parseOryResponseWithData } = useOryResponseParser();

  const handleFormSubmit = async (values: ILoginFormData | ISSOLoginFormData) => {
    if (ssoOrganizationId) {
      await samlLogin({
        loginToOryWithSAML,
        organizationId: ssoOrganizationId,
        parseOryResponseWithData,
        refreshSession: undefined,
      });
      return;
    }

    if (isILoginFormData(values)) {
      dispatch(
        actions.loginRequest({
          email: values.email,
          onUserIsInOry: async () => await loginOryUser(values, onOrySecondFactor),
          password: values.password,
        })
      );
    }
  };

  return {
    formData,
    getFormAlertMessage,
    handleFormSubmit,
    isOryApiFetching: isOrySAMLApiFetching || isOryApiFetching,
    isSSOLogin,
    retryAfterDate,
  };
}

function isILoginFormData(data: ILoginFormData | ISSOLoginFormData): data is ILoginFormData {
  return (data as ILoginFormData).password !== undefined;
}
